/**
 * Where a teacher page was opened from, so the page can say where "Back"
 * goes. A page reached from several places never infers the way back from who
 * the viewer is: the link that opened it carries an explicit origin, and the
 * page validates that origin against data it already has before it shows a
 * back link (see the shared-detail-routes-carry-explicit-origin lesson).
 *
 * Three origins, each a `from=` kind plus the ids of the page to return to:
 * - the class page: `from=students&class=<id>`, with the class page's
 *   homework in view (`homework=`), its go-over window (`window=`), its
 *   tab (`fromView=goover`) and the one student its Go over list was
 *   narrowed to (`fromStudent=<userId>`);
 * - a student's page: `from=student&class=<id>&fromStudent=<userId>`, with
 *   that page's homework in view (`homework=`), window (`window=`) and tab
 *   (`fromView=mistakes`, `exams` or `history`);
 * - a Lesson Prep worksheet: `from=worksheet&worksheet=<id>`.
 *
 * The tab rides as `fromView=`, not `view=`, so it never collides with a
 * destination's own tabs; the student as `fromStudent=`, not `student=`,
 * which Help Requests, History and Mastery already read as an auth0 id and
 * the homework page as the student a new homework is for. A default (the
 * Students tab, Overview, the last-class window, the whole class) goes
 * unsaid.
 *
 * The URL builders in `studentsUrls.ts` and `homeworkLinks.ts` write these
 * through `appendTeacherOrigin`; a destination reads them with
 * `readTeacherOrigin`, and a page that only hands them on with
 * `parseTeacherOrigin`.
 */
import {
  DEFAULT_TEACHER_STUDENTS_WINDOW,
  isClassPageWindow,
  isTeacherStudentsWindow,
} from '@shared/teacherStudentsWindows';
import { TEACHER_PAGE_RETURN_STATE } from '@/utils/teacherEventSource';

/** The class page's tabs an origin restores; Students, the default, goes unsaid. */
const CLASS_ORIGIN_VIEWS = ['goover'] as const;
export type ClassOriginView = (typeof CLASS_ORIGIN_VIEWS)[number];

/** The student page's tabs an origin restores; Overview, the default, goes unsaid. */
const STUDENT_ORIGIN_VIEWS = ['mistakes', 'exams', 'history'] as const;
export type StudentOriginView = (typeof STUDENT_ORIGIN_VIEWS)[number];

export type TeacherOrigin =
  | {
      kind: 'class';
      classCodeId: string;
      /** The homework in view on the class page, restored on the way back. */
      homeworkId?: string | null;
      /** The class page's go-over window, restored on the way back. */
      window?: string | null;
      /** The class page's tab, restored on the way back. */
      view?: ClassOriginView | null;
      /** The one student Go over was narrowed to, restored on the way back. */
      filteredStudentId?: string | null;
    }
  | {
      kind: 'student';
      studentUserId: string;
      classCodeId: string;
      /** The homework in view on the student page, restored on the way back. */
      homeworkId?: string | null;
      /** The student page's window, restored on the way back. */
      window?: string | null;
      /** The student page's tab, restored on the way back. */
      view?: StudentOriginView | null;
    }
  | { kind: 'worksheet'; worksheetId: string };

/** The class page as an origin, in the view the teacher left. */
export type ClassOrigin = Extract<TeacherOrigin, { kind: 'class' }>;

/** A student's page as an origin, in the view the teacher left. */
export type StudentOrigin = Extract<TeacherOrigin, { kind: 'student' }>;

/**
 * The origins a Lesson Prep link can carry. A worksheet origin's `worksheet=`
 * would collide with the parameter Lesson Prep opens a worksheet by.
 */
export type LessonPrepOrigin = Exclude<TeacherOrigin, { kind: 'worksheet' }>;

/**
 * Every URL parameter an origin is written in. A destination strips them
 * once it has read its way back, so a refresh doesn't replay the hop.
 */
export const TEACHER_ORIGIN_PARAMS = [
  'from',
  'class',
  'homework',
  'window',
  'fromStudent',
  'fromView',
  'worksheet',
] as const;

export interface TeacherBackLink {
  to: string;
  /** Router state: going back is a return, not a new open. */
  state: typeof TEACHER_PAGE_RETURN_STATE;
  label: string;
}

/**
 * What the destination page already knows, to check the origin against. Leave
 * out what the page doesn't load: an omitted class or worksheet list trusts a
 * well-formed id (the page it returns to answers a stale one itself), but a
 * student origin needs a name, so without `studentName` it earns no link.
 */
export interface TeacherOriginContext {
  /** The viewer's classes. */
  classIds?: Iterable<string>;
  /** The student's display name, or null when the viewer can't see them. */
  studentName?: (studentUserId: string) => string | null | undefined;
  /** Whether the viewer can see this worksheet. */
  hasWorksheet?: (worksheetId: string) => boolean;
}

const OBJECT_ID = /^[a-f\d]{24}$/i;
// The return URLs are built here rather than with `studentsUrls.ts`, which
// imports this module to write origins: no import cycle.
const CLASS_PAGE_PATH = '/teacher/students';
const LESSON_PREP_PATH = '/lesson-prep';

type Raw = string | null | undefined;

/** One of `values`, or null for anything else (a stale or hand-edited link). */
const oneOf = <T extends string>(values: readonly T[], raw: Raw): T | null =>
  (values as readonly string[]).includes(raw ?? '') ? (raw as T) : null;

/** A go-over window worth carrying: one of the class page's, not its default. */
const classWindow = (window: Raw): string | null =>
  isClassPageWindow(window) && window !== DEFAULT_TEACHER_STUDENTS_WINDOW
    ? window
    : null;

/** A window worth carrying: one of the student page's, not its default. */
const studentWindow = (window: Raw): string | null =>
  isTeacherStudentsWindow(window) && window !== DEFAULT_TEACHER_STUDENTS_WINDOW
    ? window
    : null;

/** Writes the origin's parameters onto a destination's own query. */
export function appendTeacherOrigin(
  query: URLSearchParams,
  origin: TeacherOrigin
): void {
  const setIf = (key: string, value: string | null | undefined) => {
    if (value) query.set(key, value);
  };
  switch (origin.kind) {
    case 'class':
      query.set('from', 'students');
      query.set('class', origin.classCodeId);
      setIf('homework', origin.homeworkId);
      setIf('window', classWindow(origin.window));
      setIf('fromView', oneOf(CLASS_ORIGIN_VIEWS, origin.view));
      setIf('fromStudent', origin.filteredStudentId);
      return;
    case 'student':
      query.set('from', 'student');
      query.set('class', origin.classCodeId);
      query.set('fromStudent', origin.studentUserId);
      setIf('homework', origin.homeworkId);
      setIf('window', studentWindow(origin.window));
      setIf('fromView', oneOf(STUDENT_ORIGIN_VIEWS, origin.view));
      return;
    case 'worksheet':
      query.set('from', 'worksheet');
      query.set('worksheet', origin.worksheetId);
  }
}

/**
 * The origin in a URL, by shape only, or null for none or a malformed one (a
 * stale or hand-edited link). A malformed homework, window, tab or student
 * filter is dropped rather than failing the whole return. Whether the viewer can see it is
 * `readTeacherOrigin`'s call: a page that only hands the origin on (the
 * marking page, the Save as PDF preview) needs no more than this.
 */
export function parseTeacherOrigin(
  search: URLSearchParams
): TeacherOrigin | null {
  const id = (key: string) => {
    const value = search.get(key) ?? '';
    return OBJECT_ID.test(value) ? value : null;
  };
  switch (search.get('from')) {
    case 'students': {
      const classCodeId = id('class');
      return classCodeId
        ? {
            kind: 'class',
            classCodeId,
            homeworkId: id('homework'),
            window: classWindow(search.get('window')),
            view: oneOf(CLASS_ORIGIN_VIEWS, search.get('fromView')),
            filteredStudentId: id('fromStudent'),
          }
        : null;
    }
    case 'student': {
      const classCodeId = id('class');
      const studentUserId = id('fromStudent');
      return classCodeId && studentUserId
        ? {
            kind: 'student',
            classCodeId,
            studentUserId,
            homeworkId: id('homework'),
            window: studentWindow(search.get('window')),
            view: oneOf(STUDENT_ORIGIN_VIEWS, search.get('fromView')),
          }
        : null;
    }
    case 'worksheet': {
      const worksheetId = id('worksheet');
      return worksheetId ? { kind: 'worksheet', worksheetId } : null;
    }
    default:
      return null;
  }
}

/**
 * The page an origin returns to, in the view the teacher left: the class
 * page with its homework, window, tab and student filter; a student's page
 * with its homework, window and tab; a worksheet in Lesson Prep. The
 * parameters are the destination's own (`view=` and `student=`, not
 * `fromView=` and `fromStudent=`). By shape only: whether the viewer can see
 * it is `readTeacherOrigin`'s call.
 */
export function teacherOriginHref(origin: TeacherOrigin): string {
  const query = new URLSearchParams();
  const setIf = (key: string, value: string | null | undefined) => {
    if (value) query.set(key, value);
  };
  switch (origin.kind) {
    case 'class':
      query.set('class', origin.classCodeId);
      setIf('homework', origin.homeworkId);
      setIf('window', classWindow(origin.window));
      setIf('view', oneOf(CLASS_ORIGIN_VIEWS, origin.view));
      setIf('student', origin.filteredStudentId);
      return `${CLASS_PAGE_PATH}?${query.toString()}`;
    case 'student':
      query.set('class', origin.classCodeId);
      setIf('homework', origin.homeworkId);
      setIf('window', studentWindow(origin.window));
      setIf('view', oneOf(STUDENT_ORIGIN_VIEWS, origin.view));
      return `${CLASS_PAGE_PATH}/${origin.studentUserId}?${query.toString()}`;
    case 'worksheet':
      query.set('worksheet', origin.worksheetId);
      return `${LESSON_PREP_PATH}?${query.toString()}`;
  }
}

/**
 * The back link a page opened from elsewhere shows: "Back to class", "Back to
 * {name}" or "Back to worksheet". Null when the URL carries no origin, a
 * malformed one, or one the viewer can't see.
 */
export function readTeacherOrigin(
  search: URLSearchParams,
  context: TeacherOriginContext = {}
): TeacherBackLink | null {
  const origin = parseTeacherOrigin(search);
  if (!origin) return null;
  const knowsClass = (classCodeId: string) =>
    context.classIds === undefined ||
    [...context.classIds].includes(classCodeId);
  const link = (label: string): TeacherBackLink => ({
    to: teacherOriginHref(origin),
    state: TEACHER_PAGE_RETURN_STATE,
    label,
  });

  switch (origin.kind) {
    case 'class':
      return knowsClass(origin.classCodeId) ? link('Back to class') : null;
    case 'student': {
      if (!knowsClass(origin.classCodeId)) return null;
      const name = context.studentName?.(origin.studentUserId)?.trim();
      return name ? link(`Back to ${name}`) : null;
    }
    case 'worksheet':
      return context.hasWorksheet && !context.hasWorksheet(origin.worksheetId)
        ? null
        : link('Back to worksheet');
  }
}
